import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ShippingForm, emptyShipping, validateShipping } from "@/components/ShippingForm";

const Shell = ({ title, sub, children }) => (
  <main className="grid min-h-[80vh] lg:grid-cols-2">
    <div className="nv-grain relative hidden overflow-hidden bg-black text-white lg:block">
      <div className="absolute -left-20 top-1/3 h-96 w-96 rounded-full bg-[#c6ff00]/25 blur-3xl" />
      <div className="relative flex h-full flex-col justify-center p-14">
        <div>
          <img src="/brand/icon.png" alt="" className="h-40 w-40" />
          <h2 className="mt-8 font-display text-4xl font-bold leading-tight lg:text-5xl">{title}</h2>
          <p className="mt-4 max-w-md text-white/60">{sub}</p>
        </div>
      </div>
    </div>
    <div className="flex items-center justify-center p-6 sm:p-12">
      <div className="w-full max-w-lg nv-fade-up">
        <h1 className="font-display text-3xl font-bold sm:text-4xl">{title}</h1>
        <p className="mt-2 text-black/60">{sub}</p>
        <div className="mt-8">{children}</div>
      </div>
    </div>
  </main>
);

export function Login() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [f, setF] = useState({ email: "", password: "" });
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const r = await login(f.email, f.password);
    setBusy(false);
    if (r.error) return setErr(r.error);
    toast.success(t("auth.welcome", { name: r.user.firstName }));
    nav(loc.state?.from || "/");
  };
  return (
    <Shell title={t("auth.loginTitle")} sub={t("auth.loginSub")}>
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div><label className="nv-label">{t("auth.email")}</label><input data-testid="login-email" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="nv-input" /></div>
        <div><label className="nv-label">{t("auth.password")}</label><input data-testid="login-password" type="password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="nv-input" /></div>
        {err && <p data-testid="login-error" className="rounded-lg bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{t(`auth.${err}`)}</p>}
        <button data-testid="login-submit" disabled={busy} className="nv-btn-lime w-full !py-4">{t("auth.login")} <ArrowRight size={16} /></button>
        <p className="text-center text-sm text-black/60">{t("auth.noAccount")} <Link to="/register" data-testid="go-register" className="font-bold text-black underline">{t("auth.register")}</Link></p>
      </form>
    </Shell>
  );
}

export function Register() {
  const { t } = useTranslation();
  const { register } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ ...emptyShipping, password: "" });
  const [err, setErr] = useState(null);
  const onChange = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const v = validateShipping(f);
    if (v) return setErr(v);
    if (f.password.length < 6) return setErr("errPwd");
    if (!f.referrer || f.referrer.trim().length < 2) return setErr("errReferrer");
    setBusy(true);
    const r = await register(f);
    setBusy(false);
    if (r.error) return setErr(r.error);
    toast.success(t("auth.welcome", { name: r.user.firstName }));
    nav("/account");
  };
  return (
    <Shell title={t("auth.registerTitle")} sub={t("auth.registerSub")}>
      <form onSubmit={submit} noValidate className="space-y-4" data-testid="register-form">
        <ShippingForm form={f} onChange={onChange} withReferrer />
        <div><label className="nv-label">{t("auth.password")}<span className="text-[#9ccc00]"> *</span></label><input data-testid="field-password" type="password" value={f.password} onChange={(e) => onChange("password", e.target.value)} className="nv-input" /></div>
        {err && <p data-testid="register-error" className="rounded-lg bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{t(`auth.${err}`)}</p>}
        <button data-testid="register-submit" disabled={busy} className="nv-btn-lime w-full !py-4">{t("auth.register")} <ArrowRight size={16} /></button>
        <p className="text-center text-sm text-black/60">{t("auth.haveAccount")} <Link to="/login" data-testid="go-login" className="font-bold text-black underline">{t("auth.login")}</Link></p>
      </form>
    </Shell>
  );
}
